import React from 'react';
import {
  List, Datagrid, DateField, FunctionField, SelectInput, DateInput, ReferenceInput, TopToolbar, useListContext, useNotify, useTranslate, RecordContextProvider,
} from 'react-admin';
import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';
import { OverdueBanner } from './OverdueBanner';
import { SETTLEMENT_OUTCOMES, differenceLabel, figuresLabel, settlementExportQuery, settlementRowSx, windowLabel } from './settlementFormat';
import type { KioskSettlement, SettlementOutcome } from './types';

type Translate = (key: string, options?: any) => string;

export const kioskSettlementFilters = (translate: Translate) => [
  <ReferenceInput key="siteId" source="siteId" reference="sites">
    <SelectInput optionText="name" label={translate('resources.kiosk-settlements.filters.siteId')} />
  </ReferenceInput>,
  <ReferenceInput key="kioskId" source="kioskId" reference="kiosk-devices">
    <SelectInput optionText="name" label={translate('resources.kiosk-settlements.filters.kioskId')} />
  </ReferenceInput>,
  <SelectInput
    key="outcome"
    source="outcome"
    label={translate('resources.kiosk-settlements.filters.outcome')}
    choices={SETTLEMENT_OUTCOMES.map((id) => ({ id, name: translate(`resources.kiosk-settlements.outcomes.${id}`) }))}
  />,
  <DateInput key="from" source="from" label={translate('resources.kiosk-settlements.filters.from')} />,
  <DateInput key="to" source="to" label={translate('resources.kiosk-settlements.filters.to')} />,
];

const OUTCOME_COLOR: Record<SettlementOutcome, 'success' | 'error' | 'warning'> = { ok: 'success', failed: 'error', interrupted: 'warning' };

export const OutcomeChip = ({ outcome }: { outcome: SettlementOutcome }) => {
  const translate = useTranslate();
  return <Chip size="small" color={OUTCOME_COLOR[outcome]} label={translate(`resources.kiosk-settlements.outcomes.${outcome}`)} />;
};

export const MatchCell = ({ record }: { record: KioskSettlement }) => {
  const translate = useTranslate();
  if (record.matched === null) return <Typography variant="body2" color="text.secondary">{record.failureReason === null ? '-' : record.failureReason}</Typography>;
  return (
    <Box>
      <Chip size="small" color={record.matched ? 'success' : 'error'} label={translate(`resources.kiosk-settlements.match.${record.matched ? 'matched' : 'different'}`)} />
      {!record.matched && <Typography variant="caption" display="block">{differenceLabel(record, translate)}</Typography>}
      {record.readerChanged === true && <Typography variant="caption" display="block" color="warning.main">{translate('resources.kiosk-settlements.flags.readerChanged')}</Typography>}
      {record.interruptedInWindow === true && <Typography variant="caption" display="block" color="warning.main">{translate('resources.kiosk-settlements.flags.interruptedInWindow')}</Typography>}
    </Box>
  );
};

const ExportButton = () => {
  const { filterValues } = useListContext();
  const notify = useNotify();
  const translate = useTranslate();
  const handleExport = async () => {
    try {
      const token = await tokenManager.getAccessToken();
      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/kiosk-settlements/export${settlementExportQuery(filterValues)}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!response.ok) throw new Error(`Export failed with ${response.status}`);
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'octopus-settlements.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notify(translate('resources.kiosk-settlements.messages.exportFailed'), { type: 'error' });
    }
  };
  return (
    <TopToolbar>
      <Button startIcon={<DownloadIcon />} onClick={handleExport} variant="contained" size="small">
        {translate('resources.kiosk-settlements.actions.export')}
      </Button>
    </TopToolbar>
  );
};

const MobileList = () => {
  const { data, isLoading } = useListContext<KioskSettlement>();
  const translate = useTranslate();
  if (isLoading || !data) return null;
  return (
    <Box>
      {data.map((record) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', ...settlementRowSx(record) }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{record.kioskName}</Typography>
              <OutcomeChip outcome={record.outcome} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">{`${record.siteName} · ${new Date(record.recordedAt).toLocaleString()}`}</Typography>
            <Typography variant="caption" display="block">{windowLabel(record, translate)}</Typography>
            <MatchCell record={record} />
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const KioskSettlementsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const t = (key: string) => translate(`resources.kiosk-settlements.fields.${key}`);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <OverdueBanner />
      <List
        resource="kiosk-settlements"
        filters={kioskSettlementFilters(translate)}
        actions={<ExportButton />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        title={translate('resources.kiosk-settlements.name')}
      >
        {isMobile ? (
          <MobileList />
        ) : (
          <Datagrid bulkActionButtons={false} rowClick={false} rowSx={settlementRowSx}>
            <DateField source="recordedAt" label={t('createdAt')} showTime sortable={false} />
            <FunctionField label={t('kioskName')} render={(record: KioskSettlement) => record.kioskName} />
            <FunctionField label={t('siteName')} render={(record: KioskSettlement) => record.siteName} />
            <FunctionField label={t('outcome')} render={(record: KioskSettlement) => <OutcomeChip outcome={record.outcome} />} />
            <FunctionField label={t('window')} render={(record: KioskSettlement) => windowLabel(record, translate)} />
            <FunctionField label={`${t('reader')}: ${t('deducts')}`} render={(record: KioskSettlement) => figuresLabel(record.readerDeductCount, record.readerDeductTotal, translate)} />
            <FunctionField label={`${t('ours')}: ${t('deducts')}`} render={(record: KioskSettlement) => figuresLabel(record.ourDeductCount, record.ourDeductTotal, translate)} />
            <FunctionField label={`${t('reader')}: ${t('refunds')}`} render={(record: KioskSettlement) => figuresLabel(record.readerAddValueCount, record.readerAddValueTotal, translate)} />
            <FunctionField label={`${t('ours')}: ${t('refunds')}`} render={(record: KioskSettlement) => figuresLabel(record.ourAddValueCount, record.ourAddValueTotal, translate)} />
            <FunctionField label={t('matched')} render={(record: KioskSettlement) => <MatchCell record={record} />} />
          </Datagrid>
        )}
      </List>
    </Box>
  );
};

export default KioskSettlementsList;
